import { memo, useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@bridebook/ui';
import { useMeasure } from '@uidotdev/usehooks';
import { styles } from './truncatable.style';

interface IProps {
  /**
   * Original text to show truncated if it doesn't fit. Full text will be shown in the tooltip.
   */
  text: string;
  /**
   * Track if truncated.
   */
  truncatedChanged?: (truncated: boolean) => void;
  /**
   * Number of text lines shown.
   */
  lineClamp?: number;
  /**
   * Whether to show a tooltip when the text is truncated.
   */
  withTooltip?: boolean;
}

/**
 * This component is used to truncate text when it overflows the container.
 * If the text is truncated, it will render a tooltip with the full text.
 * Otherwise, it will just render the text.
 */
const TruncatableComp = ({ text, truncatedChanged, lineClamp = 1, withTooltip = true }: IProps) => {
  const [isTruncated, setIsTruncated] = useState<boolean | undefined>(undefined);
  const [originalTextRef, { height: originalTextHeight }] = useMeasure();
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate if the text is truncated
  useEffect(() => {
    if (containerRef.current && originalTextHeight) {
      const visibleHeight = containerRef.current.offsetHeight;
      const truncated = Math.floor(originalTextHeight) > visibleHeight;
      setIsTruncated(truncated);
      truncatedChanged && truncatedChanged(truncated);
    }
  }, [text, originalTextHeight, lineClamp, truncatedChanged]);

  // Hidden span to measure the text
  const measurementSpan = (
    <Box as="span" style={styles.measurementSpan(lineClamp)} setRef={originalTextRef}>
      {text}
    </Box>
  );

  // If the text has no spaces (eg. a long email), expand tooltip width to avoid awkward breaks
  const maxWidth = text.indexOf(' ') < 0 ? 'auto' : undefined;

  return (
    <Box style={styles.container} setRef={containerRef}>
      {isTruncated && withTooltip ? (
        <Tooltip
          text={text}
          noPadding
          maxWidth={maxWidth}
          maxHeight={900}
          renderTrigger={() => (
            <Box as="span" style={styles.originalText(lineClamp)}>
              {text}
            </Box>
          )}
        />
      ) : (
        <Box as="span" style={styles.originalText(lineClamp)}>
          {text}
        </Box>
      )}
      {/* Include measurement span to detect future overflows if text/container changes */}
      {measurementSpan}
    </Box>
  );
};

export const Truncatable = memo(TruncatableComp);
